/**
 * Linear Regression Forecast Calculator
 *
 * Calculates a linear regression forecast by projecting the trend line
 * forward by the period length. Uses least squares method to fit a line
 * through the last N periods, then forecasts the value at period N.
 *
 * Formula:
 * - Slope (b) = (N * Σ(w*y) - Σw * Σy) / (N * Σ(w²) - (Σw)²)
 * - Intercept (a) = (Σy - b * Σw) / N
 * - Forecast = a + b * N
 *
 * Where:
 * - N = period (windowSize)
 * - w = weight (1, 2, 3, ... N)
 * - y = price values
 */
export default function () {
  function calculator(data) {
    const { windowSize, sourcePath } = options;

    if (!data || data.length < windowSize) {
      return new Array(data.length).fill(undefined);
    }

    // Pre-calculate constants for linear regression
    const sumWeights = (windowSize * (windowSize + 1)) / 2;
    const squaredSumWeights = Math.pow(sumWeights, 2);
    const sumWeightsSquared = (sumWeights * (2 * windowSize + 1)) / 3;

    let sumCloses = 0;
    let sumWeightedCloses = 0;
    let sumClosesSquared = 0;

    const results = [];

    for (let i = 0; i < data.length; i++) {
      const d = data[i];

      // Get source value
      const currentValue = sourcePath ? d[sourcePath] : d.close;

      if (currentValue == null) {
        results.push(undefined);
        continue;
      }

      // Update running sums
      sumWeightedCloses += windowSize * currentValue - sumCloses;
      sumCloses += currentValue;
      sumClosesSquared += Math.pow(currentValue, 2);

      // Need at least windowSize bars
      if (i < windowSize - 1) {
        results.push(undefined);
        continue;
      }

      // Remove oldest value if beyond window
      if (i > windowSize - 1) {
        const oldValue = sourcePath
          ? data[i - windowSize][sourcePath]
          : data[i - windowSize].close;

        if (oldValue != null) {
          sumCloses -= oldValue;
          sumClosesSquared -= Math.pow(oldValue, 2);
        }
      }

      // Calculate slope (b)
      const slope =
        (windowSize * sumWeightedCloses - sumWeights * sumCloses) /
        (windowSize * sumWeightsSquared - squaredSumWeights);

      // Calculate intercept (a)
      const intercept = (sumCloses - slope * sumWeights) / windowSize;

      // Calculate forecast: a + b * period
      const forecast = intercept + slope * windowSize;

      results.push(forecast);
    }

    return results;
  }

  let options = {
    windowSize: 14,
    sourcePath: "close",
  };

  calculator.undefinedLength = function () {
    return options.windowSize - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...options, ...x };
    return calculator;
  };

  return calculator;
}
